import { notify } from '@/lib/toast';
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { Wallet, Plus, TrendingDown, Tag, Trash2, Check, X } from 'lucide-react';
import { FormDialog } from '@/components/ui/form-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { formatCurrency, formatDateOnly } from '@/lib/utils';
import { isBankSettledMethod } from '@/lib/bank-accounts';
import { StatCard } from '@/components/ui/stat-card';
import { Combobox } from '@/components/ui/combobox';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { SearchInput } from '@/components/ui/search-input';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { TabBar } from '@/components/ui/tab-bar';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Label } from '@/components/ui/label';

const CATEGORY_COLORS = [
  'bg-info/10 text-info-text',
  'bg-primary/10 text-primary-text',
  'bg-warning/10 text-warning-text',
  'bg-success/10 text-success-text',
  'bg-primary/10 text-primary-text',
];
const getCategoryColor = (name: string) => CATEGORY_COLORS[name.charCodeAt(0) % CATEGORY_COLORS.length];

interface ExpenseRecord {
  id: string;
  description: string;
  amount: number | string;
  status?: string | null;
  paymentMethod?: string | null;
  reference?: string | null;
  expenseDate?: string | null;
  createdAt: string;
  category?: { name?: string | null } | null;
  bankAccount?: { name: string } | null;
}

interface ExpensesResponse {
  data?: ExpenseRecord[];
  meta?: { total?: number };
  total?: number;
}

interface ExpenseCategory {
  id: string;
  name: string;
  description?: string | null;
  _count?: { expenses?: number };
}

interface BankAccountOption {
  id: string;
  name: string;
  bankName?: string | null;
}

interface CreateExpensePayload {
  categoryId: string;
  amount: number;
  description: string;
  expenseDate: string;
  paymentMethod: string;
  bankAccountId?: string;
  reference: string;
  notes: string;
}

interface CreateCategoryPayload {
  name: string;
  description: string;
}

export function ExpensesPage() {
  const { t } = useTranslation('finance');

  const TABS = [
    { key: 'expenses', label: t('expenses') },
    { key: 'categories', label: t('categoriesTab') },
  ];

  const [activeTab, setActiveTab] = useState('expenses');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    categoryId: '', amount: '', description: '',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'CASH', bankAccountId: '', reference: '', notes: '',
  });

  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', description: '' });
  const [confirmDeleteCat, setConfirmDeleteCat] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('expenses:write') || hasPermission('expenses:*');

  const handleExpenseUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
  }, [queryClient]);
  useSocketEvent('expense.created', handleExpenseUpdate);
  useSocketEvent('expense.updated', handleExpenseUpdate);

  const { data, isLoading } = useQuery<ExpensesResponse>({
    queryKey: ['expenses', search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      const res = await api.get(`/expenses?${params}`);
      return res.data;
    },
  });

  const { data: categories, isLoading: catsLoading } = useQuery<ExpenseCategory[]>({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const res = await api.get('/expenses/categories');
      return res.data;
    },
  });

  const { data: bankAccounts } = useQuery<BankAccountOption[]>({
    queryKey: ['bank-accounts-list'],
    queryFn: async () => {
      const res = await api.get('/bank-accounts');
      return res.data?.data || res.data || [];
    },
  });

  const totalExpenses = data?.data?.reduce((sum: number, e: ExpenseRecord) => sum + Number(e.amount), 0) || 0;

  const saveMutation = useMutation({
    mutationFn: (payload: CreateExpensePayload) => api.post('/expenses', payload),
    onSuccess: () => {
      toast.success(t('expenseRecorded'));
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      setDialogOpen(false);
      setForm({
        categoryId: '', amount: '', description: '',
        expenseDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'CASH', bankAccountId: '', reference: '', notes: '',
      });
    },
    onError: (err) => notify.error(err, t('failedToAddExpense')),
  });

  const createCatMutation = useMutation({
    mutationFn: (payload: CreateCategoryPayload) => api.post('/expenses/categories', payload),
    onSuccess: () => {
      toast.success(t('categoryCreated'));
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      setCatDialogOpen(false);
      setCatForm({ name: '', description: '' });
    },
    onError: (err) => notify.error(err, t('failedToCreateCategory')),
  });

  const deleteCatMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/expenses/categories/${id}`),
    onSuccess: () => {
      toast.success(t('categoryDeleted'));
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
    },
    onError: (err) => notify.error(err, t('failedToDeleteCategory')),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/expenses/${id}/approve`),
    onSuccess: () => {
      toast.success(t('expenseApproved'));
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
    },
    onError: (err) => notify.error(err, t('failedToApproveExpense')),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.patch(`/expenses/${id}/reject`, { reason }),
    onSuccess: () => {
      toast.success(t('expenseRejected'));
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setRejectTarget(null);
    },
    onError: (err) => notify.error(err, t('failedToRejectExpense')),
  });

  // T-494: the paid-from account applies to every bank-settled method (mirrors the backend resolver);
  // it is mandatory only when the tenant has several accounts (a sole account is auto-assigned).
  const bankFieldVisible = isBankSettledMethod(form.paymentMethod);

  const handleSave = () => {
    if (!form.description || !form.amount || !form.categoryId || !form.expenseDate) {
      toast.error(t('fillRequiredFields'));
      return;
    }
    if (Number(form.amount) <= 0) {
      toast.error(t('amountMustBePositive'));
      return;
    }
    if (bankFieldVisible && (bankAccounts?.length ?? 0) > 1 && !form.bankAccountId) {
      toast.error(t('selectBankAccountForMethod'));
      return;
    }
    const payload: CreateExpensePayload = { ...form, amount: Number(form.amount) };
    if (!payload.bankAccountId) delete payload.bankAccountId;
    saveMutation.mutate(payload);
  };

  const handleCreateCategory = () => {
    if (!catForm.name.trim()) {
      toast.error(t('categoryNameRequired'));
      return;
    }
    createCatMutation.mutate(catForm);
  };

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('expenses')}
        subtitle={t('trackExpenses')}
        action={
          activeTab === 'expenses'
            ? canWrite && (
                <Button onClick={() => setDialogOpen(true)} variant="gradient">
                  <Plus className="me-2 h-4 w-4" />{t('addExpense')}
                </Button>
              )
            : canWrite && (
                <Button onClick={() => setCatDialogOpen(true)} variant="gradient">
                  <Plus className="me-2 h-4 w-4" />{t('addCategory')}
                </Button>
              )
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          className="animate-fade-in-up stagger-1"
          label={t('totalExpensesPage')}
          value={totalExpenses}
          format="currency"
          icon={TrendingDown}
          accent="danger"
        />
        <StatCard
          className="animate-fade-in-up stagger-2"
          label={t('totalEntries')}
          value={data?.meta?.total || data?.total || 0}
          format="number"
          icon={Wallet}
          accent="default"
        />
        <StatCard
          className="animate-fade-in-up stagger-3"
          label={t('categoriesLabel')}
          value={categories?.length || 0}
          format="number"
          icon={Tag}
          accent="info"
        />
      </div>

      <Card className="animate-fade-in-up stagger-4">
        <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === 'expenses' && (
          <>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t('expenseRecords')}</CardTitle>
                <SearchInput
                  value={search}
                  onChange={(value) => { setSearch(value); setPage(1); }}
                  placeholder={t('searchExpenses')}
                  className="w-64"
                />
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <SkeletonTable rows={5} cols={5} />
              ) : !data?.data?.length ? (
                <EmptyState
                  icon={Wallet}
                  title={t('noExpensesRecorded')}
                  description={t('startTrackingExpenses')}
                  action={canWrite ? { label: t('addExpense'), onClick: () => setDialogOpen(true) } : undefined}
                />
              ) : (
                <div className="overflow-auto">
                  <Table mobileCards>
                    <THead>
                      <Tr hover={false}>
                        <Th>{t('description')}</Th>
                        <Th>{t('category')}</Th>
                        <Th className="text-end">{t('amount')}</Th>
                        <Th>{t('payment')}</Th>
                        <Th>{t('date')}</Th>
                        <Th>{t('refNo')}</Th>
                        <Th>{t('status')}</Th>
                        {canWrite && <Th className="text-end">{t('actions')}</Th>}
                      </Tr>
                    </THead>
                    <TBody>
                      {data.data.map((exp: ExpenseRecord) => (
                        <Tr key={exp.id}>
                          <Td label={t('description')} className="font-medium">{exp.description}</Td>
                          <Td label={t('category')}>
                            {exp.category?.name ? (
                              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getCategoryColor(exp.category.name)}`}>
                                {exp.category.name}
                              </span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </Td>
                          <Td label={t('amount')} className="text-end font-medium text-destructive-text">
                            {formatCurrency(Number(exp.amount))}
                          </Td>
                          <Td label={t('payment')} className="text-sm text-muted-foreground">
                            {exp.bankAccount
                              ? exp.bankAccount.name
                              : exp.paymentMethod === 'CASH'
                                ? t('cash')
                                : exp.paymentMethod === 'CARD'
                                  ? t('card')
                                  : exp.paymentMethod === 'BANK_TRANSFER'
                                    ? t('bankTransfer')
                                    : exp.paymentMethod === 'MOBILE_WALLET'
                                      ? t('mobileWallet')
                                      : exp.paymentMethod === 'QR_CODE'
                                        ? t('qrCode')
                                        : exp.paymentMethod?.replace(/_/g, ' ') || '—'}
                          </Td>
                          <Td label={t('date')} className="text-muted-foreground">
                            {formatDateOnly(exp.expenseDate || exp.createdAt)}
                          </Td>
                          <Td label={t('refNo')} className="font-mono text-xs text-muted-foreground">
                            {exp.reference || '—'}
                          </Td>
                          <Td label={t('status')}>
                            <StatusBadge status={exp.status || 'POSTED'} />
                          </Td>
                          {canWrite && (
                            <Td label={t('actions')} className="text-end">
                              {exp.status === 'PENDING_APPROVAL' ? (
                                <TableRowActions
                                  actions={[
                                    {
                                      key: 'approve',
                                      label: t('approve'),
                                      icon: Check,
                                      onSelect: () => approveMutation.mutate(exp.id),
                                      disabled: approveMutation.isPending,
                                      labeled: true,
                                      primary: true,
                                    },
                                    {
                                      key: 'reject',
                                      label: t('reject'),
                                      icon: X,
                                      onSelect: () => setRejectTarget(exp.id),
                                      labeled: true,
                                      variant: 'destructive',
                                    },
                                  ]}
                                />
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </Td>
                          )}
                        </Tr>
                      ))}
                    </TBody>
                  </Table>
                  {(data?.meta || data?.total) && (
                    <div className="mt-4 border-t pt-4 px-4">
                      <PaginationBar
                        page={page}
                        pageSize={20}
                        total={data?.meta?.total || data?.total || 0}
                        onPageChange={setPage}
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </>
        )}

        {activeTab === 'categories' && (
          <>
            <CardHeader>
              <CardTitle className="text-lg">{t('expenseCategories')}</CardTitle>
            </CardHeader>
            <CardContent>
              {catsLoading ? (
                <SkeletonTable rows={5} cols={3} />
              ) : !categories?.length ? (
                <EmptyState
                  icon={Tag}
                  title={t('noCategoriesYet')}
                  description={t('createCategoriesDescription')}
                  action={canWrite ? { label: t('addCategory'), onClick: () => setCatDialogOpen(true) } : undefined}
                />
              ) : (
                <Table mobileCards>
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('name')}</Th>
                      <Th>{t('description')}</Th>
                      <Th>{t('expenses')}</Th>
                      {canWrite && <Th className="text-end">{t('actions')}</Th>}
                    </Tr>
                  </THead>
                  <TBody>
                    {categories.map((cat: ExpenseCategory) => (
                      <Tr key={cat.id}>
                        <Td label={t('name')}>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getCategoryColor(cat.name)}`}>
                            {cat.name}
                          </span>
                        </Td>
                        <Td label={t('description')} className="text-sm text-muted-foreground">
                          {cat.description || '—'}
                        </Td>
                        <Td label={t('expenses')} className="text-sm">{cat._count?.expenses ?? 0}</Td>
                        {canWrite && (
                          <Td label={t('actions')} className="text-end">
                            <TableRowActions
                              actions={[
                                {
                                  key: 'delete',
                                  label: t('deleteCategory'),
                                  icon: Trash2,
                                  onSelect: () => setConfirmDeleteCat(cat.id),
                                  variant: 'destructive',
                                  disabled: (cat._count?.expenses ?? 0) > 0,
                                },
                              ]}
                            />
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              )}
            </CardContent>
          </>
        )}
      </Card>

      {/* Add Expense Dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t('addExpense')}
        width="52rem"
        submitLabel={t('saveExpense')}
        cancelLabel={t('cancel')}
        isPending={saveMutation.isPending}
        onSubmit={(e) => { e.preventDefault(); handleSave(); }}
      >
        <div className="space-y-4 pt-2">
          <div>
            <Label className="mb-1.5 block">{t('category')} <span className="text-destructive-text">*</span></Label>
            <Combobox
              options={(categories || []).map((cat: ExpenseCategory) => ({ value: cat.id, label: cat.name }))}
              value={form.categoryId}
              onChange={(val) => setForm({ ...form, categoryId: val })}
              placeholder={t('selectCategory')}
              searchPlaceholder={t('searchCategories')}
              emptyText={t('noCategoriesAddFirst')}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('amount')} <span className="text-destructive-text">*</span></Label>
            <Input
              type="number" min="0.01" step="0.01" placeholder="0.00"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('description')} <span className="text-destructive-text">*</span></Label>
            <Input
              placeholder={t('expenseDescriptionPlaceholder')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('date')} <span className="text-destructive-text">*</span></Label>
            <Input
              type="date"
              value={form.expenseDate}
              onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('paymentMethodLabel')}</Label>
            <Combobox
              options={[
                { value: 'CASH', label: t('cash') },
                { value: 'CARD', label: t('card') },
                { value: 'BANK_TRANSFER', label: t('bankTransfer') },
                { value: 'MOBILE_WALLET', label: t('mobileWallet') },
                { value: 'QR_CODE', label: t('qrCode') },
              ]}
              value={form.paymentMethod}
              onChange={(val) => setForm({ ...form, paymentMethod: val || 'CASH', bankAccountId: '' })}
              placeholder={t('selectPaymentMethod')}
              searchPlaceholder={t('searchMethods')}
            />
          </div>
          {bankFieldVisible && (
            <div>
              <Label className="mb-1.5 block">{t('bankAccount')}{(bankAccounts?.length ?? 0) > 1 && <span className="text-destructive-text"> *</span>}</Label>
              <Combobox
                options={(bankAccounts || []).map((b: BankAccountOption) => ({ value: b.id, label: `${b.name} — ${b.bankName}` }))}
                value={form.bankAccountId}
                onChange={(val) => setForm({ ...form, bankAccountId: val || '' })}
                placeholder={t('selectBankAccount')}
                searchPlaceholder={t('searchAccounts')}
                emptyText={t('noBankAccountsYet')}
              />
            </div>
          )}
          <div>
            <Label className="mb-1.5 block">{t('refNumber')}</Label>
            <Input
              placeholder={t('referenceNoPlaceholder')}
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('notes')}</Label>
            <Textarea
              placeholder={t('notesPlaceholder')}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
            />
          </div>
        </div>
      </FormDialog>

      {/* Add Category Dialog */}
      <FormDialog
        open={catDialogOpen}
        onClose={() => setCatDialogOpen(false)}
        title={t('addExpenseCategory')}
        width="40rem"
        submitLabel={t('createCategory')}
        cancelLabel={t('cancel')}
        isPending={createCatMutation.isPending}
        onSubmit={(e) => { e.preventDefault(); handleCreateCategory(); }}
      >
        <div className="space-y-4 pt-2">
          <div>
            <Label className="mb-1.5 block">{t('name')} <span className="text-destructive-text">*</span></Label>
            <Input
              placeholder={t('categoryNamePlaceholder')}
              value={catForm.name}
              onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('description')}</Label>
            <Input
              placeholder={t('optionalDescription')}
              value={catForm.description}
              onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
            />
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDeleteCat}
        onCancel={() => setConfirmDeleteCat(null)}
        onConfirm={() => { deleteCatMutation.mutate(confirmDeleteCat!); setConfirmDeleteCat(null); }}
        title={t('deleteCategory')}
        message={t('deleteCategoryMessage')}
        confirmLabel={t('delete')}
        variant="danger"
      />

      <ConfirmDialog
        open={!!rejectTarget}
        onCancel={() => setRejectTarget(null)}
        onConfirm={() => rejectTarget && rejectMutation.mutate({ id: rejectTarget })}
        title={t('rejectExpense')}
        message={t('rejectExpenseMessage')}
        confirmLabel={t('reject')}
        variant="danger"
      />
    </div>
  );
}
